// Bounded, memory-only snapshots. Never keep drafts here or persist private data
// to disk. A late read cannot replace a successful save or repopulate after logout.
function createReadCache({ max = 120, freshFor = 30000, retainFor = 15 * 60 * 1000, now = Date.now } = {}) {
  const records = new Map()
  const get = key => {
    const record = records.get(key)
    if (record && !record.pending && now() - record.at > retainFor) {
      records.delete(key)
      return undefined
    }
    return record
  }
  const put = (key, record) => {
    records.delete(key)
    records.set(key, record)
    while (records.size > max) records.delete(records.keys().next().value)
  }
  return {
    peek(key) { return get(key)?.value },
    fresh(key) { const r = get(key); return !!r && r.value !== undefined && now() - r.at < freshFor },
    pending(key) { return !!get(key)?.pending },
    seed(key, value) { put(key, { value, at: now() }) },
    remove(key) { records.delete(key) },
    clear() { records.clear() },
    load(key, loader, { force = false } = {}) {
      let record = get(key)
      if (record?.pending) return record.pending
      if (!force && this.fresh(key)) return Promise.resolve(record.value)
      record = { value: record?.value, at: record?.at || 0 }
      put(key, record)
      record.pending = Promise.resolve().then(loader).then(value => {
        if (records.get(key) === record) {
          record.value = value
          record.at = now()
        }
        return value
      }).finally(() => { record.pending = null })
      return record.pending
    }
  }
}

function nearbyMonths(monthId) {
  if (!/^\d{4}(0[1-9]|1[0-2])$/.test(monthId)) throw new Error('Invalid timesheet month.')
  const year = Number(monthId.slice(0, 4)), month = Number(monthId.slice(4))
  // Fetch the selected month and its closest neighbours in one database query.
  return [0, -1, -2, 1].map(offset => {
    const date = new Date(Date.UTC(year, month - 1 + offset, 1))
    return `${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(2, '0')}`
  })
}

module.exports = { createReadCache, nearbyMonths }
